import React, { useState } from 'react';
import {
  Icon,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../../../shared/hooks';
import FINDINGS_MOCK_DATA from '../../../mock-data/findingsMockData';

import FINDINGS_CONSTANTS from './constants';
import type { FilterTypeState, Finding, FindingsProps } from './type';
import { FILTER_TYPE } from './type';

import './Findings.css';

const defaultFindings: Finding[] = FINDINGS_MOCK_DATA;

/**
 * Findings component displaying license management insights and recommendations
 * @description Renders a filterable list of license optimization findings with impact metrics,
 * effort levels, and cost savings information. Supports filtering by "Most Recent" or "All" findings.
 * Each finding card displays title, category, impact/effort/confidence metrics, and cost savings.
 * The component uses the inverted localization pattern for all user-facing text.
 * @param {FindingsProps} props - Component props
 * @param {Finding[]} [props.findings] - Array of finding objects to display (defaults to FINDINGS_MOCK_DATA)
 * @param {(findingId: string) => void} [props.onViewFinding] - Callback fired when a finding's view button is clicked
 * @returns {React.ReactElement} A findings container with header (h2 title and body2 subtitle), filter tabs, and findings list
 *
 * @example
 * <Findings
 *   findings={customFindings}
 *   onViewFinding={(id) => console.log('View finding:', id)}
 * />
 */
function Findings({
  findings = defaultFindings,
  onViewFinding,
  onClose,
}: FindingsProps): React.ReactElement {
  const localize = useInvertedLocalize();
  const [filterType, setFilterType] = useState<FilterTypeState>(
    FILTER_TYPE.RECENT
  );

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'Cost Reduction':
        return 'findingsCategorySuccess';

      case 'Risk Mitigation':
        return 'findingsCategoryWarning';

      case 'Compliance':
        return 'findingsCategoryInfo';

      default:
        return 'findingsCategorySuccess';
    }
  };

  const getImpactColor = (impact: string): string => {
    switch (impact) {
      case 'High':
        return 'findingsImpactHigh';

      case 'Medium':
        return 'findingsImpactMedium';

      case 'Low':
        return 'findingsImpactLow';

      default:
        return 'findingsImpactHigh';
    }
  };

  const getEffortColor = (effort: string): string => {
    switch (effort) {
      case 'High':
        return 'findingsEffortHigh';

      case 'Medium':
        return 'findingsEffortMedium';

      case 'Low':
        return 'findingsEffortLow';

      default:
        return 'findingsEffortLow';
    }
  };

  const formatSavings = (amount: number): string => amount.toLocaleString();

  return (
    <div className='findings'>
      {/* Header */}
      <div className='findingsHeader'>
        <div className='findingsHeaderTitle'>
          <Icon
            kind='SaviCompanion'
            size='medium'
            className='findingsHeaderIcon'
          />
          <Typography kind='h3'>{localize('Findings')}</Typography>
          <button
            className='findingsHeaderCloseBtn'
            onClick={onClose}
            aria-label={localize('Close findings panel')}
            type='button'>
            <Icon kind='Close' size='small' />
          </button>
        </div>
        <div className='findingsHeaderSubtitle'>
          <Typography kind='body3' className='findingsHeaderSubtitleText'>
            {localize('License Management Insights and Recommendations')}
          </Typography>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className='findingsFilterTabs'>
        <ToggleButtonGroup
          selection={filterType === FILTER_TYPE.RECENT ? 0 : 1}
          setSelection={(index: number) =>
            setFilterType(index === 0 ? FILTER_TYPE.RECENT : FILTER_TYPE.ALL)
          }>
          <ToggleButton label={localize('Most Recent')} />
          <ToggleButton label={localize('All')} />
        </ToggleButtonGroup>
      </div>

      {/* Findings List */}
      <div className='findingsList'>
        {findings.map((finding) => (
          <div key={finding.id} className='findingItem'>
            {/* Top Section */}
            <div className='findingItemTop'>
              <div className='findingItemContent'>
                <Typography kind='body3'>{finding.title}</Typography>
                <div
                  className={`findingItemCategory ${getCategoryColor(
                    finding.category
                  )}`}>
                  {finding.category}
                </div>
              </div>

              {/* Cost Section */}
              <div className='findingItemCost'>
                <Typography
                  kind='body1-bold'
                  className='findingItemSavingsAmount'>
                  {formatSavings(finding.savings)}
                </Typography>
                <Typography kind='body3' className='findingItemSavingsLabel'>
                  {finding.savingsLabel}
                </Typography>
                <button
                  className='findingItemActionButton'
                  onClick={() => onViewFinding?.(finding.id)}
                  aria-label={localize('View finding details')}
                  type='button'>
                  <Icon kind='ArrowRight' size='xSmall' />
                </button>
              </div>
            </div>

            {/* Bottom Section - Parameters */}
            <div className='findingItemBottom'>
              <div className='findingItemParameters'>
                <div className='findingItemParameter'>
                  <Typography
                    kind='body3'
                    className='findingItemParameterLabel'>
                    {localize('Impact')}
                    {FINDINGS_CONSTANTS.COLON}{' '}
                  </Typography>
                  <div
                    className={`findingItemParameterBadge ${getImpactColor(
                      finding.impact
                    )}`}>
                    <Typography kind='body3'>{finding.impact}</Typography>
                  </div>
                </div>

                <div className='findingItemParameter'>
                  <Typography
                    kind='body3'
                    className='findingItemParameterLabel'>
                    {localize('Effort')}
                    {FINDINGS_CONSTANTS.COLON}{' '}
                  </Typography>
                  <div
                    className={`findingItemParameterBadge ${getEffortColor(
                      finding.effort
                    )}`}>
                    <Typography kind='body3'>{finding.effort}</Typography>
                  </div>
                </div>

                <div className='findingItemParameter'>
                  <Typography
                    kind='body3'
                    className='findingItemParameterLabel'>
                    {localize('Timeline')}
                    {FINDINGS_CONSTANTS.COLON}{' '}
                  </Typography>
                  <Typography
                    kind='body3-bold'
                    className='findingItemTimelineValue'>
                    {finding.timeline}
                  </Typography>
                </div>
              </div>

              <div className='findingItemMetadata'>
                <div className='findingItemMetadataItem'>
                  <Typography
                    kind='body3'
                    className='findingItemParameterLabel'>
                    {localize('Confidence')}
                    {FINDINGS_CONSTANTS.COLON}{' '}
                  </Typography>
                  <Typography
                    kind='body3-bold'
                    className='findingItemConfidenceValue'>
                    {finding.confidence}
                    {FINDINGS_CONSTANTS.PERCENT}
                  </Typography>
                </div>
                <Typography kind='body3' className='findingItemParameterLabel'>
                  {FINDINGS_CONSTANTS.BULLET}
                </Typography>
                <Typography kind='body3' className='findingItemParameterLabel'>
                  {finding.vendor}
                </Typography>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Findings;
